/**
 * Shared CSS styles for fax cover page templates
 * Used by both the preview component and the generated HTML
 * Mirrors the backend's Blade template structure for accurate preview
 */
const COVER_PAGE_BASE_STYLES = `
  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }

  html,
  body {
    margin: 0;
    padding: 0;
    font-family: system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
    font-size: 1rem;
    line-height: 1.5;
    color: #212529;
  }

  h5 {
    font-size: 1.25rem !important;
    font-weight: 500 !important;
    margin-top: 0;
    margin-bottom: 0.5rem;
  }

  h6 {
    font-size: 1rem !important;
    font-weight: 500 !important;
    margin-top: 0;
    margin-bottom: 0.5rem;
  }

  p {
    margin-top: 0;
    margin-bottom: 1rem;
    font-size: 0.875rem;
  }

  .w-25 {
    width: 35%;
  }

  .p-3 {
    padding: 1rem;
  }

  .m-2 {
    margin: 0.5rem;
  }

  .border {
    border: 1px solid #6e6e6e;
  }
`;

const COVER_PAGE_PDF_STYLES = `
  ${COVER_PAGE_BASE_STYLES}

  .section-top {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    text-align: center;
    padding: 1rem;
  }

  .section-middle {
    position: fixed;
    top: 55%;
    left: 0;
    right: 0;
    padding-left: 3rem;
  }

  .section-bottom {
    position: fixed;
    bottom: 1rem;
    left: 0;
    right: 0;
    text-align: center;
    padding: 0 1rem;
  }
`;

const COVER_PAGE_PREVIEW_STYLES = `
  ${COVER_PAGE_BASE_STYLES}

  body {
    padding: 1rem;
  }

  .section-top {
    text-align: center;
    padding: 1rem;
    margin-bottom: 1rem;
  }

  .section-middle {
    padding-left: 3rem;
    margin-bottom: 1rem;
  }

  .section-bottom {
    text-align: center;
    padding: 0 1rem;
    margin-top: 2rem;
  }
`;

/**
 * Escapes HTML special characters to prevent XSS attacks
 * Converts characters like <, >, &, ", ' to their HTML entity equivalents
 * @param text The text to escape
 * @returns The escaped text safe for HTML insertion
 */
export function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Formats multi-line text by converting newlines to HTML <br> tags
 * Also escapes HTML in each line for security
 * @param text The multi-line text to format
 * @returns HTML-safe text with line breaks preserved
 */
export function formatMultiLine(text: string): string {
  return text
    .split('\n')
    .map((line) => escapeHtml(line))
    .join('<br>');
}

/**
 * Generates a complete HTML document for a fax cover page
 * Mirrors the backend Blade template structure so the preview matches the generated PDF
 * @param template The cover page template with user data
 * @param forPdf When true, uses fixed positioning + wkhtmltopdf spacer for PDF rendering
 * @returns Complete HTML document ready to be converted to PDF or displayed
 */
export function generateCoverPageHtml(
  template: {
    top_text?: string;
    organization_name?: string;
    organization_address?: string;
    phone_number?: string;
    website?: string;
    bottom_text?: string;
    logo_url?: string | null;
  },
  forPdf = false
): string {
  const to = '$destination';
  const faxNumber = '$caller_id';
  const pageCount = '$pages';
  const styles = forPdf ? COVER_PAGE_PDF_STYLES : COVER_PAGE_PREVIEW_STYLES;

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Fax Cover Page</title>
  <style>${styles}</style>
</head>
<body>
  <div class="section-top">
    ${template.top_text ? `<p>${escapeHtml(template.top_text)}</p>` : ''}
    ${template.logo_url ? `<div><img src="${template.logo_url}" alt="${template.organization_name ? escapeHtml(template.organization_name) : ''}" class="w-25 m-2" style="border: 1px solid #6e6e6e;"></div>` : ''}
    <div style="margin-top: 1rem;">
      ${template.organization_name ? `<h5>${escapeHtml(template.organization_name)}</h5>` : ''}
      ${template.organization_address ? `<h6>${formatMultiLine(template.organization_address)}</h6>` : ''}
      ${template.phone_number ? `<h6>${escapeHtml(template.phone_number)}</h6>` : ''}
      ${template.website ? `<h6>${escapeHtml(template.website)}</h6>` : ''}
    </div>
  </div>

  <div class="section-middle">
    <p><span style="font-weight: bold;">To:</span> ${to}</p>
    <p><span style="font-weight: bold;">Fax Number:</span> ${faxNumber}</p>
    <p><span style="font-weight: bold;">Number of pages (including this page):</span> ${pageCount}</p>
  </div>

  ${template.bottom_text ? `<div class="section-bottom"><p>${formatMultiLine(template.bottom_text)}</p></div>` : ''}

  ${forPdf ? '<!-- forces wkhtmltopdf to generate the page when all other elements are position:fixed -->\n  <div style="height: 297mm;"></div>' : ''}
</body>
</html>`;
}
